import { useState, type FormEvent } from "react";
import { ErroreApi, autenticazione, type AvvioSecondoFattore } from "../api";
import { Sigla } from "../componenti/Marchio";
import { useSessione } from "../sessione";

/** La verifica in due passaggi, obbligatoria per tutti.
 *
 * Tre momenti, in ordine: spiegare cosa serve, mostrare il QR e farsi dare il
 * primo codice, mostrare i codici di recupero una volta sola. **L'attivazione
 * non si accende finche' non arriva un codice giusto**: chi non riesce a
 * leggere il QR non resta chiuso fuori da un account che chiede un codice che
 * non puo' produrre. Il blocco vero e' del server; questa pagina e' la strada
 * per liberarsene.
 */
export function AttivaSecondoFattore() {
  const { rileggi, esci } = useSessione();
  const [avvio, setAvvio] = useState<AvvioSecondoFattore | null>(null);
  const [codice, setCodice] = useState("");
  const [recupero, setRecupero] = useState<string[] | null>(null);
  const [salvati, setSalvati] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [copiati, setCopiati] = useState(false);

  async function inizia() {
    setErrore(null);
    setInCorso(true);
    try {
      setAvvio(await autenticazione.avviaSecondoFattore());
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Non sono riuscito a cominciare. Riprova.");
    } finally {
      setInCorso(false);
    }
  }

  async function conferma(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    setInCorso(true);
    try {
      const { codici } = await autenticazione.confermaSecondoFattore(codice);
      setRecupero(codici);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : "Il codice non è stato accettato. Riprova.");
    } finally {
      setInCorso(false);
    }
  }

  async function copia() {
    if (recupero === null) return;
    try {
      await navigator.clipboard.writeText(recupero.join("\n"));
      setCopiati(true);
    } catch {
      // Su un indirizzo senza HTTPS il browser nega la copia: i codici sono a
      // schermo e si selezionano con un clic, basta dirlo.
      setErrore("Il browser non permette la copia: selezionali e copiali a mano.");
    }
  }

  return (
    <main className="portineria">
      <div className="portineria-foglio">
        <Sigla bicolore className="accesso-sigla" />
        <p className="accesso-studio">Studio Ceruzzi</p>
        <h1>Verifica in due passaggi</h1>

        {recupero !== null ? (
          <>
            <p className="portineria-sottotitolo">
              Attivata. Questi sono i tuoi codici di recupero: servono se perdi il
              telefono, ciascuno vale una volta sola.{" "}
              <strong>Salvali adesso, non si possono rivedere.</strong>
            </p>
            <ul className="codici-recupero">
              {recupero.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            {errore && (
              <p className="errore" role="alert">
                {errore}
              </p>
            )}
            <div>
              <button className="tasto" type="button" onClick={copia}>
                {copiati ? "Copiati" : "Copia i codici"}
              </button>
            </div>
            <label className="aiuto">
              <input
                type="checkbox"
                checked={salvati}
                onChange={(e) => setSalvati(e.target.checked)}
              />{" "}
              Li ho salvati in un posto sicuro.
            </label>
            <div>
              <button
                className="tasto tasto-principale"
                type="button"
                disabled={!salvati}
                onClick={() => void rileggi()}
              >
                Continua
              </button>
            </div>
          </>
        ) : avvio === null ? (
          <>
            <p className="portineria-sottotitolo">
              Per accedere serve, oltre alla password, un codice che cambia ogni
              trenta secondi e che vedi sul telefono. Ti serve un'app di autenticazione:
              Microsoft Authenticator, Google Authenticator, o il gestore di
              password se sa farlo.
            </p>
            {errore && (
              <p className="errore" role="alert">
                {errore}
              </p>
            )}
            <div>
              <button
                className="tasto tasto-principale"
                type="button"
                disabled={inCorso}
                onClick={() => void inizia()}
              >
                {inCorso ? "Un momento" : "Comincia"}
              </button>
              <button className="tasto tasto-nudo" type="button" onClick={() => void esci()}>
                Esci
              </button>
            </div>
          </>
        ) : (
          <form className="modulo" onSubmit={conferma} noValidate>
            <p className="portineria-sottotitolo">
              Apri l'app e inquadra questo codice. Poi scrivi qui sotto il codice a
              sei cifre che l'app ti mostra.
            </p>
            <img
              className="qr-segreto"
              alt="Codice QR da inquadrare con l'app di autenticazione"
              src={`data:image/svg+xml;base64,${avvio.qr_svg_base64}`}
            />
            <p className="aiuto">
              Se non puoi inquadrarlo, inserisci nell'app questa chiave:{" "}
              <span className="chiave-manuale">{avvio.chiave}</span>
            </p>

            <div className="campo">
              <label htmlFor="codice">Codice a sei cifre</label>
              <input
                id="codice"
                value={codice}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                required
                onChange={(e) => setCodice(e.target.value)}
              />
            </div>

            {errore && (
              <p className="errore" role="alert">
                {errore}
              </p>
            )}

            <div>
              <button className="tasto tasto-principale" type="submit" disabled={inCorso}>
                {inCorso ? "Sto controllando" : "Attiva"}
              </button>
              <button className="tasto tasto-nudo" type="button" onClick={() => void esci()}>
                Esci
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
